import { useState, useMemo } from 'react';
import { Box } from '@mui/material';

import { Label, DataTable, Loading, ErrorMessage, type TableColumn } from '@/src/ui';
import type { ClassOverviewResultDTO } from '../models/classOverviewDTOs';
import StudentAttemptsModal from '@/src/pages/reports/studentHistory/components/StudentAttemptsModal';
import AttemptDetailsModal from '@/src/pages/exams/attemptDetails/components/AttemptDetailsModal';
import {
    ReportCellStyle,
    ReportHeaderStyle,
    Constants,
    utcToLocal,
    formatOptions,
} from '@/src/utils';

interface StudentHistoryModalState {
    studentId: number;
    examId: number;
    attemptNumber: number;
    maxAttempt: number;
}
interface AttemptDetailsModalState {
    studentId: number;
    examId: number;
}

interface ClassOverviewTableProps {
    data: ClassOverviewResultDTO[];
    isLoading: boolean;
    error: string;
    emptyMessage?: string;
    pdfFileName?: string | (() => string);
}

const ClassOverviewTable: React.FC<ClassOverviewTableProps> = (props) => {
    const { data, isLoading, error, emptyMessage, pdfFileName } = props;

    const [studentHistoryModalState, setStudentHistoryModalState] =
        useState<StudentHistoryModalState | null>(null);
    const [attemptDetailsModalState, setAttemptDetailsModalState] =
        useState<AttemptDetailsModalState | null>(null);

    const columns = useMemo<TableColumn[]>(
        () => [
            {
                dataKey: 'studentName',
                header: 'Student Name',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassOverviewResultDTO) => {
                    if (!row.studentId || !row.examId) return '-';
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setStudentHistoryModalState({
                                    studentId: row.studentId!,
                                    examId: row.examId!,
                                    attemptNumber: 1,
                                    maxAttempt: row.attemptCount,
                                });
                            }}
                        >
                            {row.studentName}
                        </a>
                    );
                },
            },
            {
                dataKey: 'attemptCount',
                header: 'Attempt Count',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassOverviewResultDTO) => {
                    if (!row.attemptCount) return '-';
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setAttemptDetailsModalState({
                                    studentId: row.studentId!,
                                    examId: row.examId!,
                                });
                            }}
                        >
                            {row.attemptCount}
                        </a>
                    );
                },
            },
            {
                dataKey: 'completedDate',
                header: 'Completed Date',
                allowSort: true,
                align: 'center',
                formatter: (row: ClassOverviewResultDTO) => {
                    if (!row.completedDate) return '-';
                    return utcToLocal(row.completedDate).toLocaleString(undefined, formatOptions);
                },
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'percentCorrect',
                header: 'Percent Correct',
                allowSort: true,
                align: 'center',
                formatter: (row: ClassOverviewResultDTO) =>
                    row.percentCorrect != null ? `${row.percentCorrect}%` : '-',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'scoringBand',
                header: 'Scoring Band',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassOverviewResultDTO) => {
                    const band = row.scoringBand;
                    if (!band) return '-';
                    return <Label color={Constants.bandColors[band]}>{band}</Label>;
                },
            },
        ],
        []
    );

    if (isLoading) {
        return <Loading message="Loading class overview..." />;
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
                <ErrorMessage error={error} />
            </Box>
        );
    }

    return (
        <div className="w-full overflow-auto">
            <DataTable
                data={data.filter((row) => row.studentId && row.examId)}
                columns={columns}
                tableName="ClassOverviewTable"
                rowsPerPageInitial={50}
                emptyMessage={emptyMessage}
                enableExportCSV
                enableExportExcel
                enableExportPDF
                pdfFileName={pdfFileName}
                alternatingRowColor
                defaultSortColumn="studentName"
                defaultSortDirection="asc"
            />
            {studentHistoryModalState && (
                <StudentAttemptsModal
                    studentId={studentHistoryModalState.studentId}
                    examId={studentHistoryModalState.examId}
                    attemptNumber={studentHistoryModalState.attemptNumber}
                    maxAttempt={studentHistoryModalState.maxAttempt}
                    open={studentHistoryModalState !== null}
                    onClose={() => setStudentHistoryModalState(null)}
                />
            )}
            {attemptDetailsModalState && (
                <AttemptDetailsModal
                    studentId={attemptDetailsModalState.studentId}
                    examId={attemptDetailsModalState.examId}
                    open={attemptDetailsModalState !== null}
                    onClose={() => setAttemptDetailsModalState(null)}
                />
            )}
        </div>
    );
};

export default ClassOverviewTable;
